import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    MdClose, MdEmail, MdLock, MdVisibility, MdVisibilityOff, MdCheckCircle, MdLockReset
} from 'react-icons/md';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { useLanguage } from '../context/LanguageContext';

export default function ForgotPasswordModal({ onClose }) {
    const { t, language } = useLanguage();
    const [step, setStep] = useState('email'); // email -> code -> newPassword -> success

    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState('');
    const [sending, setSending] = useState(false);

    const [code, setCode] = useState('');
    const [codeError, setCodeError] = useState('');
    const [verifying, setVerifying] = useState(false);
    const [resendCooldown, setResendCooldown] = useState(0);
    const [verificationToken, setVerificationToken] = useState('');

    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
    const [passwordErrors, setPasswordErrors] = useState({});
    const [resetting, setResetting] = useState(false);

    useEffect(() => {
        if (resendCooldown <= 0) return;
        const timer = setInterval(() => {
            setResendCooldown(s => (s <= 1 ? 0 : s - 1));
        }, 1000);
        return () => clearInterval(timer);
    }, [resendCooldown]);

    const handleSendCode = async (e) => {
        e.preventDefault();
        if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
            setEmailError(t('register.emailInvalid'));
            return;
        }
        setEmailError('');
        setSending(true);
        try {
            await api.post('/auth/forgot-password/send-code', {
                email: email.trim(),
                language
            });
            toast.success(t('register.codeSentSuccess'));
            setCode('');
            setCodeError('');
            setResendCooldown(60);
            setStep('code');
        } catch (err) {
            setEmailError(err.response?.data?.message || t('forgotPassword.emailNotFound'));
        } finally {
            setSending(false);
        }
    };

    const handleVerifyCode = async (e) => {
        e.preventDefault();
        if (!code.trim() || code.trim().length !== 6) {
            setCodeError(t('forgotPassword.invalidCode'));
            return;
        }
        setVerifying(true);
        setCodeError('');
        try {
            const res = await api.post('/auth/forgot-password/verify-code', {
                email: email.trim(),
                code: code.trim()
            });
            setVerificationToken(res.data.data.verificationToken);
            setNewPassword('');
            setConfirmNewPassword('');
            setPasswordErrors({});
            setStep('newPassword');
        } catch (err) {
            setCodeError(err.response?.data?.message || t('forgotPassword.invalidCode'));
        } finally {
            setVerifying(false);
        }
    };

    const handleResend = async () => {
        if (resendCooldown > 0) return;
        setVerifying(true);
        try {
            await api.post('/auth/forgot-password/send-code', {
                email: email.trim(),
                language
            });
            toast.success(t('register.codeSentSuccess'));
            setResendCooldown(60);
        } catch (err) {
            toast.error(err.response?.data?.message || t('forgotPassword.emailNotFound'));
        } finally {
            setVerifying(false);
        }
    };

    const handleBackToEmail = () => {
        setStep('email');
        setCode('');
        setCodeError('');
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        const errs = {};
        if (!newPassword || newPassword.length < 6) errs.newPassword = t('forgotPassword.passwordMin');
        if (confirmNewPassword !== newPassword) errs.confirmNewPassword = t('forgotPassword.passwordMismatch');
        if (Object.keys(errs).length > 0) {
            setPasswordErrors(errs);
            return;
        }

        setResetting(true);
        try {
            await api.post('/auth/forgot-password/reset', {
                email: email.trim(),
                verificationToken,
                newPassword
            });
            setStep('success');
        } catch (err) {
            toast.error(err.response?.data?.message || t('forgotPassword.invalidCode'));
        } finally {
            setResetting(false);
        }
    };

    return (
        <AnimatePresence>
            <motion.div
                className="modal-overlay"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
                style={{ alignItems: 'flex-start', paddingTop: 24, paddingBottom: 24, overflowY: 'auto' }}
            >
                <motion.div
                    className="modal"
                    style={{ maxWidth: 440, width: '100%', margin: 'auto' }}
                    initial={{ opacity: 0, scale: 0.92, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.92, y: 20 }}
                    transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
                >
                    {step === 'success' ? (
                        <div style={{ padding: '48px 32px', textAlign: 'center' }}>
                            <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                                style={{
                                    width: 72, height: 72,
                                    borderRadius: '50%',
                                    background: 'rgba(16,185,129,0.15)',
                                    border: '2px solid rgba(16,185,129,0.4)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    margin: '0 auto 20px', fontSize: 36, color: 'var(--success)'
                                }}
                            >
                                <MdCheckCircle />
                            </motion.div>
                            <h2 style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 22, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 12 }}>
                                {t('forgotPassword.successTitle')}
                            </h2>
                            <p style={{ color: 'var(--text-secondary)', fontSize: 14, lineHeight: 1.7, marginBottom: 28 }}>
                                {t('forgotPassword.successMessage')}
                            </p>
                            <button className="login-btn" style={{ maxWidth: 220, margin: '0 auto' }} onClick={onClose}>
                                {t('forgotPassword.backToLogin')}
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="modal-header">
                                <div className="modal-title">
                                    <div style={{
                                        width: 36, height: 36, borderRadius: 'var(--radius-sm)',
                                        background: 'rgba(79,142,247,0.15)', color: 'var(--primary)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18
                                    }}>
                                        {step === 'newPassword' ? <MdLockReset /> : <MdEmail />}
                                    </div>
                                    {step === 'newPassword' ? t('forgotPassword.newPasswordTitle')
                                        : step === 'code' ? t('forgotPassword.verifyTitle')
                                        : t('forgotPassword.title')}
                                </div>
                                <button className="modal-close" onClick={onClose} id="forgot-password-modal-close">
                                    <MdClose />
                                </button>
                            </div>

                            <div className="modal-body">
                                {step === 'email' && (
                                    <form onSubmit={handleSendCode} noValidate>
                                        <p style={{ color: 'var(--text-secondary)', fontSize: 14, lineHeight: 1.6, marginBottom: 20 }}>
                                            {t('forgotPassword.emailStepDescription')}
                                        </p>
                                        <div className="form-group">
                                            <label className="form-label" htmlFor="forgot-email">
                                                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                    <MdEmail style={{ fontSize: 14 }} /> {t('forgotPassword.emailLabel')} *
                                                </span>
                                            </label>
                                            <input
                                                id="forgot-email"
                                                className="form-input"
                                                type="email"
                                                placeholder={t('forgotPassword.emailPlaceholder')}
                                                value={email}
                                                onChange={e => { setEmail(e.target.value); if (emailError) setEmailError(''); }}
                                                disabled={sending}
                                                autoFocus
                                            />
                                            {emailError && <div className="form-error">{emailError}</div>}
                                        </div>

                                        <button
                                            id="forgot-password-send-btn"
                                            type="submit"
                                            className="login-btn"
                                            disabled={sending}
                                            style={{ marginTop: 8 }}
                                        >
                                            {sending ? (
                                                <span style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
                                                    <span className="spinner spinner-sm" style={{ borderColor: 'rgba(255,255,255,0.3)', borderTopColor: 'white' }} />
                                                    {t('forgotPassword.sendingCode')}
                                                </span>
                                            ) : t('forgotPassword.sendCodeButton')}
                                        </button>
                                    </form>
                                )}

                                {step === 'code' && (
                                    <form onSubmit={handleVerifyCode} noValidate>
                                        <p style={{ color: 'var(--text-secondary)', fontSize: 14, lineHeight: 1.6, marginBottom: 20 }}>
                                            {t('forgotPassword.codeSent', { email: email.trim() })}
                                        </p>
                                        <div className="form-group">
                                            <input
                                                id="forgot-password-code"
                                                className="form-input"
                                                type="text"
                                                inputMode="numeric"
                                                maxLength={6}
                                                placeholder={t('forgotPassword.codePlaceholder')}
                                                value={code}
                                                onChange={e => {
                                                    setCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                                                    if (codeError) setCodeError('');
                                                }}
                                                disabled={verifying}
                                                style={{ textAlign: 'center', fontSize: 22, letterSpacing: 6 }}
                                                autoFocus
                                            />
                                            {codeError && <div className="form-error">{codeError}</div>}
                                        </div>

                                        <button
                                            id="forgot-password-verify-btn"
                                            type="submit"
                                            className="login-btn"
                                            disabled={verifying}
                                            style={{ marginTop: 8 }}
                                        >
                                            {verifying ? (
                                                <span style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
                                                    <span className="spinner spinner-sm" style={{ borderColor: 'rgba(255,255,255,0.3)', borderTopColor: 'white' }} />
                                                    {t('forgotPassword.verifying')}
                                                </span>
                                            ) : t('forgotPassword.verifyButton')}
                                        </button>

                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, fontSize: 13 }}>
                                            <button
                                                type="button"
                                                onClick={handleBackToEmail}
                                                disabled={verifying}
                                                style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', padding: 0, fontFamily: 'Inter, sans-serif' }}
                                            >
                                                {t('forgotPassword.backToEmail')}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleResend}
                                                disabled={verifying || resendCooldown > 0}
                                                style={{
                                                    background: 'none', border: 'none', cursor: resendCooldown > 0 ? 'default' : 'pointer',
                                                    color: resendCooldown > 0 ? 'var(--text-muted)' : 'var(--primary)',
                                                    padding: 0, fontFamily: 'Inter, sans-serif', fontWeight: 600
                                                }}
                                            >
                                                {resendCooldown > 0
                                                    ? t('forgotPassword.resendWait', { seconds: resendCooldown })
                                                    : t('forgotPassword.resendCode')}
                                            </button>
                                        </div>
                                    </form>
                                )}

                                {step === 'newPassword' && (
                                    <form onSubmit={handleResetPassword} noValidate>
                                        <div className="form-group">
                                            <label className="form-label" htmlFor="forgot-new-password">
                                                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                    <MdLock style={{ fontSize: 14 }} /> {t('forgotPassword.newPassword')} *
                                                </span>
                                            </label>
                                            <div style={{ position: 'relative' }}>
                                                <input
                                                    id="forgot-new-password"
                                                    className="form-input"
                                                    type={showNewPassword ? 'text' : 'password'}
                                                    placeholder={t('forgotPassword.newPasswordPlaceholder')}
                                                    value={newPassword}
                                                    onChange={e => {
                                                        setNewPassword(e.target.value);
                                                        if (passwordErrors.newPassword) setPasswordErrors(p => ({ ...p, newPassword: '' }));
                                                    }}
                                                    autoComplete="new-password"
                                                    disabled={resetting}
                                                    style={{ paddingRight: 40 }}
                                                    autoFocus
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowNewPassword(s => !s)}
                                                    tabIndex={-1}
                                                    style={{
                                                        position: 'absolute', right: 12, top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        background: 'none', border: 'none',
                                                        color: 'var(--text-muted)', cursor: 'pointer', fontSize: 17,
                                                        display: 'flex', alignItems: 'center', padding: 0
                                                    }}
                                                >
                                                    {showNewPassword ? <MdVisibilityOff /> : <MdVisibility />}
                                                </button>
                                            </div>
                                            {passwordErrors.newPassword && <div className="form-error">{passwordErrors.newPassword}</div>}
                                        </div>

                                        <div className="form-group">
                                            <label className="form-label" htmlFor="forgot-confirm-password">
                                                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                    <MdLock style={{ fontSize: 14 }} /> {t('forgotPassword.confirmNewPassword')} *
                                                </span>
                                            </label>
                                            <div style={{ position: 'relative' }}>
                                                <input
                                                    id="forgot-confirm-password"
                                                    className="form-input"
                                                    type={showConfirmNewPassword ? 'text' : 'password'}
                                                    placeholder={t('forgotPassword.confirmNewPasswordPlaceholder')}
                                                    value={confirmNewPassword}
                                                    onChange={e => {
                                                        setConfirmNewPassword(e.target.value);
                                                        if (passwordErrors.confirmNewPassword) setPasswordErrors(p => ({ ...p, confirmNewPassword: '' }));
                                                    }}
                                                    autoComplete="new-password"
                                                    disabled={resetting}
                                                    style={{ paddingRight: 40 }}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowConfirmNewPassword(s => !s)}
                                                    tabIndex={-1}
                                                    style={{
                                                        position: 'absolute', right: 12, top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        background: 'none', border: 'none',
                                                        color: 'var(--text-muted)', cursor: 'pointer', fontSize: 17,
                                                        display: 'flex', alignItems: 'center', padding: 0
                                                    }}
                                                >
                                                    {showConfirmNewPassword ? <MdVisibilityOff /> : <MdVisibility />}
                                                </button>
                                            </div>
                                            {passwordErrors.confirmNewPassword && <div className="form-error">{passwordErrors.confirmNewPassword}</div>}
                                        </div>

                                        <button
                                            id="forgot-password-reset-btn"
                                            type="submit"
                                            className="login-btn"
                                            disabled={resetting}
                                            style={{ marginTop: 8 }}
                                        >
                                            {resetting ? (
                                                <span style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
                                                    <span className="spinner spinner-sm" style={{ borderColor: 'rgba(255,255,255,0.3)', borderTopColor: 'white' }} />
                                                    {t('forgotPassword.resetting')}
                                                </span>
                                            ) : t('forgotPassword.resetButton')}
                                        </button>
                                    </form>
                                )}
                            </div>
                        </>
                    )}
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}
